import { useMemo } from 'react';
import { Mail } from '../api/mails/types';
import { getPeriodLabel } from '../utils/dateUtils';
import { useTranslation } from './useTranslation';

export interface GroupedMails {
    period: string;
    mails: Mail[];
}

interface UseGroupedMailsParams {
    mails: Mail[];
}

/**
 * Определяет период письма
 * @param mail - Письмо
 * @param currentDate - Текущая дата
 * @param locale - Локаль для форматирования
 * @param labels - Объект с переводами для "этой недели" и "этого месяца"
 * @returns Название периода
 */
const getMailPeriod = (
    mail: Mail,
    currentDate: Date,
    locale: string,
    labels: { thisWeek: string; thisMonth: string },
): string => {
    return getPeriodLabel(new Date(mail.created_at), currentDate, locale, labels);
};

/**
 * Хук для группировки писем по периодам
 * Использует тот факт, что письма уже отсортированы от новых к старым
 */
export const useGroupedMails = ({ mails }: UseGroupedMailsParams): GroupedMails[] => {
    const { locale, t } = useTranslation();

    return useMemo(() => {
        const currentDate = new Date();
        const thisWeekLabel = t.pages.mail.thisWeek;
        const thisMonthLabel = t.pages.mail.thisMonth;

        // Инициализируем группы в правильном порядке
        const thisWeekGroup: Mail[] = [];
        const thisMonthGroup: Mail[] = [];
        const monthGroups = new Map<string, Mail[]>();

        // Проходим по уже отсортированному массиву и распределяем письма по группам
        mails.forEach((mail) => {
            const period = getMailPeriod(mail, currentDate, locale, {
                thisWeek: thisWeekLabel,
                thisMonth: thisMonthLabel,
            });

            if (period === thisWeekLabel) {
                thisWeekGroup.push(mail);
            } else if (period === thisMonthLabel) {
                thisMonthGroup.push(mail);
            } else {
                // Группа по месяцу
                if (!monthGroups.has(period)) {
                    monthGroups.set(period, []);
                }
                monthGroups.get(period)!.push(mail);
            }
        });

        // Собираем результат в правильном порядке
        const grouped: GroupedMails[] = [];

        // 1. Эта неделя
        if (thisWeekGroup.length > 0) {
            grouped.push({ period: thisWeekLabel, mails: thisWeekGroup });
        }

        // 2. Этот месяц
        if (thisMonthGroup.length > 0) {
            grouped.push({ period: thisMonthLabel, mails: thisMonthGroup });
        }

        // 3. Остальные месяцы (уже отсортированы по дате первого письма благодаря порядку в массиве)
        monthGroups.forEach((mailsInMonth, period) => {
            grouped.push({ period, mails: mailsInMonth });
        });

        return grouped;
    }, [mails, locale, t]);
};
